'use client';

import { useEffect } from 'react';
import { captureException } from '@sentry/nextjs';

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Root-layout render errors bypass app/error.tsx and land here; without
    // this explicit capture they never reach Vitals. Errors with a `digest`
    // were already captured server-side by onRequestError and arrive here as
    // Next's redacted placeholder — skip those to avoid a second,
    // ungroupable event.
    if (!error.digest) {
      try {
        captureException(error);
      } catch {
        /* last-resort boundary: a throwing reporter would blank the page */
      }
    }
    // Unconditional, matching app/error.tsx. Local builds do not report to
    // Vitals at all (lib/vitals-shared.ts#shouldReportToVitals), so this is
    // the ONLY signal a developer gets for a root-layout crash on a local
    // production build — where there is no dev overlay either.
    console.error(error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          backgroundColor: '#000',
          color: '#fff',
          fontFamily: 'system-ui, sans-serif',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          textAlign: 'center',
          padding: '24px',
        }}
      >
        <h1 style={{ fontSize: '24px', fontWeight: 600 }}>
          Something went wrong
        </h1>
        <p style={{ opacity: 0.7, fontSize: '14px' }}>
          An unexpected error occurred. Please refresh the page or try again
          later.
        </p>
        {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- root error boundary renders its own <html> outside router context; next/link is unavailable here */}
        <a
          href="/"
          style={{
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.4)',
            borderRadius: '999px',
            padding: '10px 24px',
            textDecoration: 'none',
            fontSize: '14px',
          }}
        >
          Back to home
        </a>
      </body>
    </html>
  );
}
